import axios from 'axios';
import {
    AdminAlert,
    AdminAlertListResponse,
    AdminAlertUnreadCountResponse,
} from '../types/adminAlert';

const USE_MOCK =
    process.env.REACT_APP_USE_ALERT_MOCK === 'true' ||
    (typeof window !== 'undefined' && window.localStorage?.getItem('USE_ALERT_MOCK') === 'true');

const headers = (accessToken: string) => ({ authorization: accessToken });

const buildUrl = (base: string, params: Record<string, string | number | boolean | undefined>) => {
    const qs = Object.entries(params)
        .filter(([, value]) => value !== undefined && value !== '')
        .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
        .join('&');
    return qs ? `${base}?${qs}` : base;
};

// ─────────────────────────────────────────────────────────────
// Mock store (백엔드 미구현 동안 UI 검증용)
// localStorage 'USE_ALERT_MOCK=true' 또는 REACT_APP_USE_ALERT_MOCK=true 일 때만 활성화
// ─────────────────────────────────────────────────────────────
const minutesAgo = (m: number) => new Date(Date.now() - m * 60 * 1000).toISOString();
const hoursAgo = (h: number) => new Date(Date.now() - h * 60 * 60 * 1000).toISOString();
const daysAgo = (d: number) => new Date(Date.now() - d * 24 * 60 * 60 * 1000).toISOString();

let mockStore: AdminAlert[] = [
    {
        id: 'mock-1',
        centerId: 'mock-center',
        adminId: null,
        type: 'NEW_OPPORTUNITY',
        title: '강사 광고 신규 신청',
        body: '한길 회원이 길강사에게 OT 신청',
        metadata: {
            opportunityId: 'opp_mock_1',
            coachId: 'cch_mock',
            memberId: null,
            categoryName: 'OT',
        },
        isRead: false,
        readAt: null,
        createdAt: minutesAgo(1),
    },
    {
        id: 'mock-2',
        centerId: 'mock-center',
        adminId: null,
        type: 'COMM_BOX_NEW',
        title: '소통상자 신규 문의',
        body: '김회원: 이용권 환불 가능한가요?',
        metadata: { commBoxId: 'cb_mock_1', memberId: 'mem_mock_1' },
        isRead: false,
        readAt: null,
        createdAt: minutesAgo(7),
    },
    {
        id: 'mock-3',
        centerId: 'mock-center',
        adminId: null,
        type: 'COMM_BOX_REPLY',
        title: '소통상자 답글 도착',
        body: '박회원이 답글에 추가 질문을 남겼습니다',
        metadata: { commBoxId: 'cb_mock_2', memberId: 'mem_mock_2' },
        isRead: false,
        readAt: null,
        createdAt: minutesAgo(22),
    },
    {
        id: 'mock-4',
        centerId: 'mock-center',
        adminId: null,
        type: 'PAYMENT_FAIL',
        title: '결제 실패 알림',
        body: '이회원의 카드 자동결제가 실패했습니다',
        metadata: { memberId: 'mem_mock_3', paymentId: 'pay_mock_1', failReason: '한도 초과' },
        isRead: true,
        readAt: hoursAgo(1),
        createdAt: hoursAgo(2),
    },
    {
        id: 'mock-5',
        centerId: 'mock-center',
        adminId: null,
        type: 'BAD_REVIEW',
        title: '부정 리뷰 도착',
        body: '최회원이 길강사에게 별점 2점 리뷰를 남겼습니다',
        metadata: { reviewId: 'rev_mock_1', memberId: 'mem_mock_4', coachId: 'cch_mock', rating: 2 },
        isRead: true,
        readAt: daysAgo(1),
        createdAt: daysAgo(1),
    },
    {
        id: 'mock-6',
        centerId: 'mock-center',
        adminId: null,
        type: 'SCHEDULE_NO_SHOW',
        title: '노쇼 처리',
        body: '김회원 · 길강사 PT 수업이 노쇼 처리되었습니다',
        metadata: {
            source: 'PT',
            memberId: 'mem_mock_1',
            memberIds: ['mem_mock_1'],
            memberCount: 1,
            coachId: 'cch_mock',
            scheduleId: 'sch_mock_1',
            sessionId: null,
            className: null,
            startTime: hoursAgo(3),
            scheduleDate: null,
            actorRole: 'coach',
        },
        isRead: false,
        readAt: null,
        createdAt: minutesAgo(10),
    },
];

const mockApi = {
    list: (params: { limit?: number; cursor?: string; unreadOnly?: boolean }): AdminAlertListResponse => {
        const filtered = params.unreadOnly ? mockStore.filter((a) => !a.isRead) : mockStore;
        const limit = params.limit ?? 20;
        const items = filtered.slice(0, limit);
        return { items, nextCursor: null, hasMore: filtered.length > items.length };
    },
    unreadCount: (): AdminAlertUnreadCountResponse => ({
        count: mockStore.filter((a) => !a.isRead).length,
    }),
    markRead: (id: string): void => {
        const target = mockStore.find((a) => a.id === id);
        if (target && !target.isRead) {
            target.isRead = true;
            target.readAt = new Date().toISOString();
        }
    },
    markAllRead: (): number => {
        let updated = 0;
        const now = new Date().toISOString();
        mockStore = mockStore.map((a) => {
            if (!a.isRead) {
                updated += 1;
                return { ...a, isRead: true, readAt: now };
            }
            return a;
        });
        return updated;
    },
};

// ─────────────────────────────────────────────────────────────
// Real API
// ─────────────────────────────────────────────────────────────

export const getAdminAlertsAPI = async (
    accessToken: string,
    params: { limit?: number; cursor?: string; unreadOnly?: boolean } = {},
): Promise<AdminAlertListResponse> => {
    if (USE_MOCK) {
        return mockApi.list(params);
    }
    const response = await axios({
        method: 'get',
        url: buildUrl(`${process.env.REACT_APP_SERVER}/v1/admin/alerts`, params),
        headers: headers(accessToken),
    });
    return response.data?.data ?? { items: [], nextCursor: null, hasMore: false };
};

export const getAdminAlertsUnreadCountAPI = async (
    accessToken: string,
): Promise<AdminAlertUnreadCountResponse> => {
    if (USE_MOCK) {
        return mockApi.unreadCount();
    }
    const response = await axios({
        method: 'get',
        url: `${process.env.REACT_APP_SERVER}/v1/admin/alerts/unread-count`,
        headers: headers(accessToken),
    });
    return response.data?.data ?? { count: 0 };
};

export const markAdminAlertReadAPI = async (
    alertId: string,
    accessToken: string,
): Promise<void> => {
    if (USE_MOCK) {
        mockApi.markRead(alertId);
        return;
    }
    await axios({
        method: 'patch',
        url: `${process.env.REACT_APP_SERVER}/v1/admin/alerts/${alertId}/read`,
        headers: headers(accessToken),
    });
};

export const markAllAdminAlertsReadAPI = async (
    accessToken: string,
): Promise<{ updatedCount: number }> => {
    if (USE_MOCK) {
        return { updatedCount: mockApi.markAllRead() };
    }
    const response = await axios({
        method: 'patch',
        url: `${process.env.REACT_APP_SERVER}/v1/admin/alerts/read-all`,
        headers: headers(accessToken),
    });
    return response.data?.data ?? { updatedCount: 0 };
};

export const isAdminAlertMockEnabled = (): boolean => USE_MOCK;
